import React, { useState } from 'react';
import { AlertTriangle, Users, Check, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Maintenance panel that surfaces apprentice licences which are no
 * longer attached to an active master (orphans). Lets the admin
 * reassign or detach in bulk. Extracted from AdminPanel.jsx on
 * 28/05/2026.
 */
export default function OrphanApprenticesPanel({ orphans, masterLicenses, token, onChanged }) {
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkTarget, setBulkTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Reset selection state when leaving bulk mode.
  const toggleBulk = () => {
    setBulkMode(b => !b);
    setSelectedIds(new Set());
    setBulkTarget('');
    setError('');
  };

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(orphans.map(o => o.id)));
  };

  const clearAll = () => setSelectedIds(new Set());

  const assignSingle = async (orphanId, masterId) => {
    if (!masterId) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/licenses/${orphanId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ master_license_id: masterId }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Erreur');
      }
      onChanged();
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const bulkAssign = async () => {
    if (selectedIds.size === 0 || !bulkTarget) { setError('Sélectionne au moins 1 apprenant et une cohorte.'); return; }
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/licenses/bulk-assign-master`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ license_ids: Array.from(selectedIds), master_license_id: bulkTarget }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Erreur');
      }
      setSelectedIds(new Set());
      setBulkTarget('');
      onChanged();
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  return (
    <div className="bg-ink-800 border border-slate-600 rounded-xl overflow-hidden" data-testid="orphan-apprentices">
      <div className="bg-slate-800/40 px-5 py-3 border-b border-slate-600 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-400" />
          <h3 className="font-semibold text-slate-300 text-sm">Apprenants indépendants</h3>
          <span className="text-xs text-slate-500">({orphans.length})</span>
        </div>
        <div className="flex items-center gap-2">
          {bulkMode && (
            <>
              <button onClick={selectAll} className="text-[10px] px-2 py-1 rounded bg-ink-700/60 text-slate-300 hover:bg-ink-700" data-testid="orphan-select-all">
                Tout sélectionner
              </button>
              <button onClick={clearAll} className="text-[10px] px-2 py-1 rounded bg-ink-700/60 text-slate-400 hover:bg-ink-700" data-testid="orphan-clear">
                Effacer
              </button>
            </>
          )}
          <button
            onClick={toggleBulk}
            className={`text-[10px] px-2.5 py-1 rounded transition-colors ${
              bulkMode
                ? 'bg-purple-500/20 text-purple-200 border border-purple-400'
                : 'bg-ink-700/60 text-slate-300 hover:bg-ink-700 border border-ink-600'
            }`}
            data-testid="orphan-toggle-bulk"
          >
            {bulkMode ? 'Annuler la sélection' : 'Sélection multiple'}
          </button>
        </div>
      </div>

      {/* Bulk action bar */}
      {bulkMode && (
        <div className="px-5 py-3 bg-purple-500/5 border-b border-purple-500/20 flex items-center gap-2 flex-wrap" data-testid="bulk-action-bar">
          <span className="text-xs font-mono text-purple-300">{selectedIds.size} sélectionné·s</span>
          <span className="text-xs text-slate-500">→</span>
          <select
            value={bulkTarget}
            onChange={e => setBulkTarget(e.target.value)}
            disabled={selectedIds.size === 0 || busy}
            className="bg-background text-slate-100 border border-purple-400/60 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:opacity-50"
            data-testid="bulk-target-select"
          >
            <option value="">— Choisir une cohorte —</option>
            {(masterLicenses || []).map(m => (
              <option key={m.id} value={m.id}>
                {m.holder_name}{m.establishment ? ` · ${m.establishment}` : ''}
              </option>
            ))}
          </select>
          <button
            onClick={bulkAssign}
            disabled={selectedIds.size === 0 || !bulkTarget || busy}
            className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-md font-semibold disabled:opacity-50"
            data-testid="bulk-assign-submit"
          >
            <Send className="w-3 h-3 inline mr-1" />
            Assigner les {selectedIds.size} sélectionné·s
          </button>
          {error && <span className="text-xs text-danger ml-2" data-testid="bulk-error">{error}</span>}
        </div>
      )}

      <div className="p-4 space-y-2">
        {orphans.map(o => {
          const isSelected = selectedIds.has(o.id);
          return (
            <div
              key={o.id}
              className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 transition-colors ${
                bulkMode && isSelected
                  ? 'bg-purple-500/10 border-purple-400'
                  : 'bg-ink-700/30 border-ink-600 hover:border-slate-500'
              }`}
              data-testid={`orphan-row-${o.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {bulkMode && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelected(o.id)}
                    className="w-3.5 h-3.5 accent-purple-500 cursor-pointer flex-shrink-0"
                    data-testid={`orphan-check-${o.id}`}
                  />
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-100 truncate">{o.holder_name}</p>
                  <code className="font-mono text-[10px] text-slate-500">{o.license_key}</code>
                </div>
              </div>
              {!bulkMode && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <label className="text-[10px] text-slate-500 font-mono">Assigner à :</label>
                  <select
                    onChange={(e) => { if (e.target.value) assignSingle(o.id, e.target.value); e.target.value = ''; }}
                    disabled={busy || (masterLicenses || []).length === 0}
                    defaultValue=""
                    className="bg-background text-slate-100 border border-ink-600 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:opacity-50"
                    data-testid={`orphan-assign-${o.id}`}
                  >
                    <option value="">— Choisir cohorte —</option>
                    {(masterLicenses || []).map(m => (
                      <option key={m.id} value={m.id}>
                        {m.holder_name}{m.establishment ? ` · ${m.establishment}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {(masterLicenses || []).length === 0 && (
        <p className="text-[10px] text-amber-400 px-4 pb-3">
          Crée d'abord une licence Responsable Pédagogique avant de pouvoir assigner ces apprenants.
        </p>
      )}
    </div>
  );
}
